import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "@/lib/cn";

export type ButtonGroupProps = HTMLAttributes<HTMLDivElement> & {
  /**
   * Visually joins the child buttons into a single connected control (a split
   * button): inner corners are squared and adjacent borders are collapsed.
   * Defaults to `true`. Set `false` for a spaced row of independent buttons.
   */
  attached?: boolean;
};

/**
 * Groups two or more `Button`/`IconButton` children into one connected control.
 * The canonical use is a split button — a primary action plus an adjacent
 * trigger (e.g. "Add Order" + a caret menu). The first child keeps its left
 * radius, the last child keeps its right radius, inner corners are squared, and
 * the 1px border overlap is collapsed so the segments read as one unit.
 */
export const ButtonGroup = forwardRef<HTMLDivElement, ButtonGroupProps>(
  ({ className, attached = true, role = "group", children, ...props }, ref) => (
    <div
      ref={ref}
      role={role}
      className={cn(
        "bfrs-inline-flex bfrs-items-center",
        attached
          ? "[&>*:not(:first-child)]:bfrs-rounded-l-none [&>*:not(:last-child)]:bfrs-rounded-r-none [&>*:not(:first-child)]:-bfrs-ml-px [&>*:focus-visible]:bfrs-relative [&>*:focus-visible]:bfrs-z-10"
          : "bfrs-gap-2",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
);

ButtonGroup.displayName = "ButtonGroup";
